import React, { useEffect, useMemo, useState } from "react";
import ModalComponent from "../../../components/Tenant/ModalComponent/ModalComponent";
import SelectComponentWithSearch from "../../../components/Tenant/SelectComponentWIthSearch/SelectComponentWithSearch";
import { toast } from "react-hot-toast";
import Papa from "papaparse";
import {
  HiOutlineCloudUpload,
  HiOutlineDownload,
  HiOutlineUserAdd,
  HiOutlineCheckCircle,
} from "react-icons/hi";
import {
  useSearchUsersForSchemeAssignmentMutation,
  useAssignSchemeToUsersMutation,
  useBulkAssignSchemeToUsersMutation,
} from "../../../features/tenant/Aqualite/aqualiteApi";
import { downloadSampleCSV } from "../../../utils/downloadSampleCsv";
import AppLoader from "../../../components/Loader/AppLoader";
import ViewSchemeUsersModal from "./ViewSchemeUsersModal";

const STEPS = ["Select Scheme", "Add Users"];

const EMPTY_SCHEME = { id: -1, name: "", raw: null };

const AddSchemeUsersModal = ({ show, setShow, schemes = [] }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedScheme, setSelectedScheme] = useState(EMPTY_SCHEME);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [file, setFile] = useState(null);
  const [uploadMode, setUploadMode] = useState("search");
  const [assignResult, setAssignResult] = useState(null);
  const [showViewUsers, setShowViewUsers] = useState(false);

  const tenant_id = JSON.parse(sessionStorage.getItem("vendorData")).tenant_id;
  const token = sessionStorage.getItem("access_token");

  const [searchUsers, { isLoading: searchLoading }] =
    useSearchUsersForSchemeAssignmentMutation();
  const [assignUsers, { isLoading: assignLoading }] =
    useAssignSchemeToUsersMutation();
  const [bulkAssign, { isLoading: bulkLoading }] =
    useBulkAssignSchemeToUsersMutation();

  const schemeOptions = useMemo(
    () =>
      schemes.map((s) => ({
        id: s.id,
        name: `${s.name} (ID: ${s.id})`,
        raw: s,
      })),
    [schemes],
  );

  const resetModal = () => {
    setActiveStep(0);
    setSelectedScheme(EMPTY_SCHEME);
    setSearchQuery("");
    setSearchResults([]);
    setSelectedUsers([]);
    setFile(null);
    setUploadMode("search");
    setAssignResult(null);
    setShowViewUsers(false);
    setShow(false);
  };

  useEffect(() => {
    if (!show) return;
    setActiveStep(0);
    setSelectedScheme(EMPTY_SCHEME);
    setSearchQuery("");
    setSearchResults([]);
    setSelectedUsers([]);
    setFile(null);
    setUploadMode("search");
    setAssignResult(null);
    setShowViewUsers(false);
  }, [show]);

  const handleSearch = async () => {
    if (!searchQuery || searchQuery.trim().length < 3) {
      toast.error("Enter at least 3 characters to search");
      return;
    }

    try {
      const response = await searchUsers({
        token,
        tenant_id,
        body: { mobile: searchQuery.trim() },
      }).unwrap();

      if (response?.status) {
        setSearchResults(response.body || []);
        if (!response.body?.length) {
          toast.error("No users found");
        }
      } else {
        toast.error(response?.message || "Search failed");
      }
    } catch (error) {
      toast.error(error?.data?.message || "Search failed");
    }
  };

  const isUserSelected = (id) => selectedUsers.some((u) => u.id === id);

  const toggleUser = (user) => {
    if (isUserSelected(user.id)) {
      setSelectedUsers((prev) => prev.filter((u) => u.id !== user.id));
    } else {
      setSelectedUsers((prev) => [...prev, user]);
    }
  };

  const handleAssign = async () => {
    if (!selectedScheme?.id || selectedScheme.id === -1) {
      toast.error("Select a scheme first");
      return;
    }

    try {
      if (uploadMode === "csv") {
        if (!file) {
          toast.error("Please select a CSV file");
          return;
        }

        const response = await bulkAssign({
          token,
          tenant_id,
          file,
          scheme_id: String(selectedScheme.id),
        }).unwrap();

        if (response?.status) {
          setAssignResult(response.body);
          toast.success(response.message || "Users assigned successfully");
        } else {
          toast.error(response?.message || "Assignment failed");
        }
        return;
      }

      if (!selectedUsers.length) {
        toast.error("Select at least one user");
        return;
      }

      const response = await assignUsers({
        token,
        tenant_id,
        body: {
          scheme_id: selectedScheme.id,
          user_ids: selectedUsers.map((u) => u.id),
        },
      }).unwrap();

      if (response?.status) {
        setAssignResult(response.body);
        toast.success(response.message || "Users assigned successfully");
      } else {
        toast.error(response?.message || "Assignment failed");
      }
    } catch (error) {
      toast.error(error?.data?.message || "Assignment failed");
    }
  };

  const handleDownloadSummary = () => {
    if (!assignResult) return;

    const rows = [
      ...(assignResult.updated || []).map((r) => ({
        app_user_id: r.app_user_id,
        name: r.name,
        mobile: r.mobile,
        status: "updated",
        scheme_ids_after: (r.scheme_ids_after || []).join("|"),
      })),
      ...(assignResult.skipped || []).map((r) => ({
        app_user_id: r.app_user_id,
        name: r.name,
        mobile: r.mobile,
        status: "already_had_scheme",
        scheme_ids_after: (r.scheme_ids || []).join("|"),
      })),
      ...(assignResult.not_found_mobiles || []).map((m) => ({
        app_user_id: "",
        name: "",
        mobile: m,
        status: "not_found",
        scheme_ids_after: "",
      })),
    ];

    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `scheme_users_${selectedScheme?.id}_${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const isSubmitting = assignLoading || bulkLoading;

  return (
    <ModalComponent
      show={show}
      setShow={setShow}
      size="4xl"
      title="Add Users to Scheme"
    >
      <div className="p-4">
        <div className="flex items-center justify-center mb-6 gap-4">
          {STEPS.map((label, idx) => (
            <div key={label} className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                  idx <= activeStep
                    ? "bg-primary text-white"
                    : "bg-gray-200 text-gray-600"
                }`}
              >
                {idx < activeStep ? <HiOutlineCheckCircle /> : idx + 1}
              </div>
              <span
                className={`text-sm font-medium ${
                  idx <= activeStep ? "text-gray-900" : "text-gray-500"
                }`}
              >
                {label}
              </span>
              {idx < STEPS.length - 1 && (
                <div className="w-12 h-0.5 bg-gray-300 mx-2" />
              )}
            </div>
          ))}
        </div>

        {assignResult ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <h4 className="font-semibold text-green-800 mb-2">Assignment Complete</h4>
              <p className="text-sm text-green-700">
                Scheme: <strong>{assignResult.scheme_name}</strong> (ID: {assignResult.scheme_id})
              </p>
              <ul className="mt-2 text-sm text-green-700 space-y-1">
                <li>Updated: {assignResult.updated?.length || 0}</li>
                <li>Already had scheme: {assignResult.skipped?.length || 0}</li>
                <li>
                  Not found:{" "}
                  {(assignResult.not_found_mobiles?.length || 0) +
                    (assignResult.not_found?.length || 0)}
                </li>
              </ul>
            </div>

            
            <div className="flex gap-2 justify-end">
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={handleDownloadSummary}
              >
                <HiOutlineDownload className="inline mr-1" />
                Download Summary
              </button>
              <button className="btn btn-primary btn-sm" onClick={resetModal}>
                Close
              </button>
            </div>
          </div>
        ) : activeStep === 0 ? (
          <div className="space-y-4">
            <SelectComponentWithSearch
              title="Select Scheme"
              placeholder="Choose scheme..."
              required
              selected={selectedScheme}
              setSelected={(item) => {
                setSelectedScheme(
                  item && item.id !== -1
                    ? { id: item.id, name: item.name, raw: item.raw }
                    : EMPTY_SCHEME,
                );
              }}
              dataArr={schemeOptions}
            />

            {selectedScheme?.raw?.settings?.type === "scheme_id_check" ? (
              <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded p-2">
                This scheme has user restriction enabled. Only assigned users will get scheme benefits.
              </p>
            ) : selectedScheme?.id !== -1 ? (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                Note: This scheme does not have &quot;Restrict to assigned users&quot; enabled.
                Enable it while creating/editing the scheme for the check to apply.
              </p>
            ) : null}

            {selectedScheme?.raw?.settings?.type === "scheme_id_check" && (
              <div className="flex justify-end">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => setShowViewUsers(true)}
                >
                  View Users
                </button>
              </div>
            )}

            <div className="flex justify-end">
              <button
                className="btn btn-primary"
                disabled={!selectedScheme || selectedScheme.id === -1}
                onClick={() => setActiveStep(1)}
              >
                Next
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">

            <div className="flex gap-2 border-b pb-2">
              <button
                className={`px-3 py-1 text-sm rounded ${
                  uploadMode === "search"
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-gray-700"
                }`}
                onClick={() => setUploadMode("search")}
              >
                Search by Phone
              </button>
              <button
                className={`px-3 py-1 text-sm rounded ${
                  uploadMode === "csv"
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-gray-700"
                }`}
                onClick={() => setUploadMode("csv")}
              >
                Upload CSV
              </button>
            </div>

            {uploadMode === "search" ? (
              <>
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="Search by mobile (min 3 digits)..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                  <button
                    className="btn btn-outline-primary whitespace-nowrap"
                    onClick={handleSearch}
                    disabled={searchLoading}
                  >
                    Search
                  </button>
                </div>

                {searchLoading && <AppLoader text="Searching..." size="sm" />}

                {searchResults.length > 0 && (
                  <div className="border rounded-lg max-h-48 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="p-2 text-left w-10"></th>
                          <th className="p-2 text-left">Name</th>
                          <th className="p-2 text-left">Mobile</th>
                          <th className="p-2 text-left">Type</th>
                          <th className="p-2 text-left">Scheme IDs</th>
                        </tr>
                      </thead>
                      <tbody>
                        {searchResults.map((user) => (
                          <tr key={user.id} className="border-t hover:bg-gray-50">
                            <td className="p-2">
                              <input
                                type="checkbox"
                                checked={isUserSelected(user.id)}
                                onChange={() => toggleUser(user)}
                              />
                            </td>
                            <td className="p-2">{user.name}</td>
                            <td className="p-2">{user.mobile}</td>
                            <td className="p-2">{user.user_type}</td>
                            <td className="p-2">
                              {user.scheme_ids?.length
                                ? user.scheme_ids.join(", ")
                                : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {selectedUsers.length > 0 && (
                  <div className="border rounded-lg p-3 bg-gray-50">
                    <h4 className="text-sm font-semibold mb-2">
                      Selected Users ({selectedUsers.length})
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedUsers.map((u) => (
                        <span
                          key={u.id}
                          className="inline-flex items-center gap-1 bg-white border rounded-full px-3 py-1 text-xs"
                        >
                          {u.name} ({u.mobile})
                          <button
                            className="text-red-500 font-bold ml-1"
                            onClick={() => toggleUser(u)}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <HiOutlineCloudUpload className="mx-auto h-10 w-10 text-gray-400" />
                <p className="mt-2 text-sm text-gray-600">
                  Upload CSV with a <strong>mobile</strong> column (10-digit numbers)
                </p>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="mt-3 text-sm"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
                <button
                  className="btn btn-link btn-sm mt-2"
                  onClick={() =>
                    downloadSampleCSV([{ name: "mobile" }])
                  }
                >
                  <HiOutlineDownload className="inline mr-1" />
                  Download Sample CSV
                </button>
              </div>
            )}

            <div className="flex justify-between pt-2">
              <button
                className="btn btn-outline-secondary"
                onClick={() => setActiveStep(0)}
              >
                Back
              </button>
              <button
                className="btn btn-primary flex items-center gap-1"
                onClick={handleAssign}
                disabled={isSubmitting}
              >
                <HiOutlineUserAdd />
                {isSubmitting ? "Assigning..." : "Assign Users"}
              </button>
            </div>
          </div>
        )}
      </div>

      {showViewUsers && selectedScheme?.id !== -1 && (
        <ViewSchemeUsersModal
          show={showViewUsers}
          setShow={setShowViewUsers}
          scheme={{ id: selectedScheme.id, name: selectedScheme.raw?.name || selectedScheme.name }}
        />
      )}
    </ModalComponent>
  );
};

export default AddSchemeUsersModal;
